import { forwardRef } from 'react'

import type { LinkProps } from 'next/link'
import Link from 'next/link'
import type { PropsWithChildren } from 'react'

import { useCountryFromRegionProvider } from '@cash-web/shared-data-access-region-provider'
import { defaultRegion } from '@cash-web/support/utils-region-config'

interface RegionalisedLinkProps
  extends Omit<LinkProps, 'href'>,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> {
  href: LinkProps['href']
  onClick?: (event: { preventDefault: () => void }) => void
}

const getRegionalisedHref = (href: LinkProps['href'], region: string): LinkProps['href'] => {
  if (typeof href === 'string') {
    const isInternalPath = href.startsWith('/')
    return isInternalPath ? `/${region}${href}` : href
  }

  // UrlObject
  const pathname = href.pathname ?? ''
  const isInternalPath = pathname.startsWith('/')
  if (!isInternalPath) return href

  return {
    ...href,
    pathname: `/${region}${pathname}`,
  }
}

const RegionalisedLink = forwardRef<HTMLAnchorElement, PropsWithChildren<RegionalisedLinkProps>>(
  ({ children, ...props }, ref) => {
    const region = useCountryFromRegionProvider()?.toLowerCase() ?? defaultRegion

    return (
      <Link {...props} href={getRegionalisedHref(props.href, region)} ref={ref}>
        {children}
      </Link>
    )
  }
)

RegionalisedLink.displayName = 'RegionalisedLink'

export default RegionalisedLink
